/** @format */

import {
  GeneralHtmlSupportConfig,
  HeadingConfig,
  HighlightConfig,
  ImageConfig,
  LinkConfig,
  ListConfig,
  MediaEmbedConfig,
  TableConfig,
  ToolbarConfig,
  Editor,
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  Essentials,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HtmlEmbed,
  Image,
  ImageResizeEditing,
  ImageResizeButtons,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageInsert,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  Table,
  TableToolbar,
  TableCellProperties,
  Undo,
  CloudServices,
  ImageUpload,
  TextTransformation,
  SpecialCharacters,
  SpecialCharactersCurrency,
  SpecialCharactersLatin,
  Superscript,
  Subscript,
  RemoveFormat,
  EditorConfig,
  PluginConstructor,
} from "ckeditor5";
import {
  AnnotationsUIs,
  PresenceList,
  Comments,
  CommentsRepository,
  RealTimeCollaborativeComments,
  RealTimeCollaborativeEditing,
  RealTimeCollaborativeTrackChanges,
  TrackChanges,
  TrackChangesData,
  TrackChangesEditing,
} from "ckeditor5-premium-features";
import MathType from "@wiris/mathtype-ckeditor5/dist/index.js";
import { GlossaryTerms } from "@edres/ckeditor5-glossary";
import { Youtube } from "@edres/ckeditor5-youtube";
import { Unsplash } from "@edres/ckeditor5-unsplash";

const defaultMediaEmbed: MediaEmbedConfig = {
  previewsInData: true,
  removeProviders: ["instagram", "twitter", "googleMaps", "flickr", "facebook"],
  providers: [
    {
      name: "youtube",
      url: [
        /^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)[?=&a-z0-9]*/,
        /^(?:m\.)?youtube\.com\/v\/([\w-]+)[?=&a-z0-9]*/,
        /^youtube\.com\/embed\/([\w-]+)[?=&a-z0-9]*/,
        /^youtu\.be\/([\w-]+)[?=&a-z0-9]*/,
      ],
      html: (match: any) => {
        const id = match[1];
        const startmatch = match[0].match(/start=(\d+)/);
        const endmatch = match[0].match(/end=(\d+)/);
        const start = startmatch && startmatch.length > 1 ? startmatch[1] : "0";
        const end = endmatch && endmatch.length > 1 ? endmatch[1] : "";
        return (
          `<div class="video-container youtube">` +
          `<iframe ` +
          `src="https://www.youtube.com/embed/${id}?rel=0&&start=${start}&end=${end}"` +
          'allowfullscreen allow="autoplay; encrypted-media" frameborder="0">' +
          "</iframe>" +
          "</div>"
        );
      },
    },
    {
      name: "vimeo",
      url: [
        /^vimeo\.com\/(\d+)([?=&a-z0-9]*)/,
        /^vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)([?=&a-z0-9]*)/,
        /^vimeo\.com\/album\/[^/]+\/video\/(\d+)([?=&a-z0-9]*)/,
        /^vimeo\.com\/channels\/[^/]+\/(\d+)([?=&a-z0-9]*)/,
        /^vimeo\.com\/groups\/[^/]+\/videos\/(\d+)([?=&a-z0-9]*)/,
        /^vimeo\.com\/ondemand\/[^/]+\/(\d+)([?=&a-z0-9]*)/,
        /^player\.vimeo\.com\/video\/(\d+)([?=&a-z0-9]*)/,
      ],
      html: (match: any) => {
        const id = match[1];
        const querystring = match.length > 2 ? match[2] : "";
        return (
          '<div class="video-container vimeo">' +
          `<iframe src="https://player.vimeo.com/video/${id}${querystring}" ` +
          'frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen>' +
          "</iframe>" +
          "</div>"
        );
      },
    },
    {
      name: "dailymotion",
      url: [
        /^dailymotion\.com\/video\/(\w+)/,
        /^dailymotion\.com\/embed\/video\/(\w+)/,
      ],
      html: (match: any) => {
        const id = match[1];

        return (
          '<div  class="video-container dailymotion">' +
          `<iframe src="https://www.dailymotion.com/embed/video/${id}" ` +
          'frameborder="0" allowfullscreen allow="autoplay">' +
          "</iframe>" +
          "</div>"
        );
      },
    },
    {
      name: "audiofile",
      url: [/https?:\/\/.+\.(?:mp3|ogg|oga|wav)$/],
      html: (match: any) =>
        '<div class="audio-container audio">' +
        `<audio controls src="${match[0]}" />` +
        "</div>",
    },
    {
      name: "videofile",
      url: [/https?:\/\/.+\.(?:mp4|ogv|webm)$/],
      html: (match: any) =>
        '<div class="video-container video">' +
        `<video controls src="${match[0]}" />` +
        "</div>",
    },
  ],
};

const defaultHTMLSupport: GeneralHtmlSupportConfig = {
  allow: [
    {
      name: "span",
      attributes: true,
      classes: true,
      styles: true,
    },
    {
      name: "img",
      attributes: true,
      classes: true,
      styles: "",
    },
  ],
};

const defaultHeading: HeadingConfig = {
  options: [
    { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
    {
      model: "heading1",
      view: "h3",
      title: "Heading 1",
      class: "ck-heading_heading1",
    },
    {
      model: "heading2",
      view: "h4",
      title: "Heading 2",
      class: "ck-heading_heading2",
    },
  ],
};

const defaultImage: ImageConfig = {
  resizeUnit: "%",
  resizeOptions: [
    {
      name: "resizeImage:original",
      label: "Original",
      value: null,
    },
    {
      name: "resizeImage:25",
      label: "25%",
      value: "25",
    },
    {
      name: "resizeImage:50",
      label: "50%",
      value: "50",
    },
    {
      name: "resizeImage:75",
      label: "75%",
      value: "75",
    },
  ],
  styles: {
    options: [
      {
        name: "alignLeft",
        modelElements: ["imageBlock"],
        icon: "left",
        title: "Align left",
        className: "image-style-align-left",
      },
      {
        name: "alignCenter",
        modelElements: ["imageBlock"],
        icon: "center",
        title: "Centre image",
        className: "img-center",
      },
      {
        name: "alignRight",
        modelElements: ["imageBlock"],
        icon: "right",
        title: "Align right",
        className: "image-style-align-right",
      },
      {
        name: "custom-block",
        modelElements: ["imageBlock"],
        icon: "full",
        title: "Image block",
        className: "img-block",
      },
    ],
  },
  toolbar: [
    "imageStyle:custom-block",
    "|",
    "imageStyle:alignLeft",
    "imageStyle:alignCenter",
    "imageStyle:alignRight",
    "|",
    "resizeImage",
    "|",
    "imageTextAlternative",
    "toggleImageCaption",
    "|",
    "linkImage",
  ],
};

const defaultLink: LinkConfig = {
  decorators: {
    openInNewTab: {
      mode: "manual",
      label: "Open in a new tab",
      defaultValue: true,
      attributes: {
        target: "_blank",
        rel: "noopener noreferrer",
      },
    },
  },
};

const defaultTable: TableConfig = {
  contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
};

const defaultList: ListConfig = {
  properties: {
    styles: true,
    startIndex: true,
    reversed: true,
  },
};

const defaultHighlight: HighlightConfig = {
  options: [
    {
      model: "greenMarker",
      class: "marker-green",
      title: "Green marker",
      color: "var(--ck-highlight-marker-green)",
      type: "marker",
    },
    {
      model: "pinkMarker",
      class: "marker-pink",
      title: "Pink marker",
      color: "var(--ck-highlight-marker-pink)",
      type: "marker",
    },
    {
      model: "blueMarker",
      class: "marker-blue",
      title: "Blue marker",
      color: "var(--ck-highlight-marker-blue)",
      type: "marker",
    },
  ],
};

const publicPlugins: Array<PluginConstructor<Editor>> = [
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  Essentials,
  GeneralHtmlSupport,
  GlossaryTerms,
  Heading,
  Highlight,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResizeButtons,
  ImageResizeEditing,
  ImageStyle,
  ImageToolbar,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersCurrency,
  SpecialCharactersLatin,
  Subscript,
  Superscript,
  Table,
  TableToolbar,
  Undo,
  Unsplash,
];

const publicToolbar: ToolbarConfig = {
  items: [
    "undo",
    "redo",
    "|",
    "heading",
    "|",
    "alignment",
    "|",
    "outdent",
    "indent",
    "|",
    "bold",
    "italic",
    "removeFormat",
    {
      label: "More basic styles",
      icon: "threeVerticalDots",
      items: ["superscript", "subscript", "specialCharacters", "highlight"],
    },
    "|",
    {
      label: "Lists",
      icon: false,
      items: ["bulletedList", "numberedList"],
    },
    "|",
    "link",
    "|",
    "blockQuote",
    "glossary",
    "|",
    "insertTable",
    "insertImageViaUrl",
    "mediaEmbed",
    "unsplash",
  ],
  shouldNotGroupWhenFull: true,
};

const publicConfig: EditorConfig = {
  plugins: publicPlugins,
  toolbar: publicToolbar,
  mediaEmbed: defaultMediaEmbed,
  image: defaultImage,
  htmlSupport: defaultHTMLSupport,
  table: defaultTable,
  heading: defaultHeading,
  link: defaultLink,
  list: defaultList,
  highlight: defaultHighlight,
};

const staffConfig: EditorConfig = {
  plugins: [
    ...publicPlugins,
    AnnotationsUIs,
    CloudServices,
    Comments,
    CommentsRepository,
    HtmlEmbed,
    ImageUpload,
    MathType,
    PresenceList,
    RealTimeCollaborativeComments,
    RealTimeCollaborativeEditing,
    RealTimeCollaborativeTrackChanges,
    TableCellProperties,
    TextTransformation,
    TrackChanges,
    TrackChangesData,
    TrackChangesEditing,
    Youtube,
  ],
  toolbar: {
    items: [
      ...(publicToolbar.items ?? []),
      "youtube",
      "|",
      "MathType",
      "ChemType",
      "|",
      "comment",
      "commentsArchive",
      "trackChanges",
    ],
    shouldNotGroupWhenFull: true,
  },
  licenseKey:
    "RTcwdE9NaHh2UFY1TkFMeDJ4OWlMOXlwN0h2bWxwSjgyNGQyTVdBZmlxQnFVZ2tSbEcrUVRvVEs3ZC9oMHNSNktGVHF5czR2Sng2ZndPdnQtTWpBeU5ERXdNVE09",
  comments: {
    editorConfig: {
      plugins: [Autoformat, Bold, Italic, List],
    },
  },
  link: defaultLink,
  mediaEmbed: defaultMediaEmbed,
  image: defaultImage,
  htmlSupport: defaultHTMLSupport,
  table: {
    ...defaultTable,
    contentToolbar: [
      ...(defaultTable?.contentToolbar ?? []),
      "tableCellProperties",
    ],
    tableCellProperties: {
      defaultProperties: {
        borderStyle: "solid",
        borderColor: "#e9ecef",
        borderWidth: "1px",
      },
      backgroundColors: [],
      borderColors: [],
      colorPicker: false,
    },
  },
  heading: defaultHeading,
  list: defaultList,
  highlight: defaultHighlight,
};

export const CKEditorConfigs = { publicConfig, staffConfig };
